import { create } from 'zustand'
import * as Dialog from '@radix-ui/react-dialog'
import { cn } from '@/lib/utils'

// -------------------------------------------------------
// Dialog di conferma globale, coerente col design system.
// Sostituisce window.confirm() nativo. Uso:
//   import { confirm } from '@/lib/confirm'
//   if (await confirm({ title: 'Eliminare?', message: '...', danger: true })) { ... }
// -------------------------------------------------------

export interface ConfirmOptions {
  title: string
  message?: string
  confirmLabel?: string
  cancelLabel?: string
  danger?: boolean
}

interface ConfirmStore {
  open: boolean
  opts: ConfirmOptions | null
  resolve: ((value: boolean) => void) | null
}

const useConfirmStore = create<ConfirmStore>(() => ({
  open: false,
  opts: null,
  resolve: null,
}))

export function confirm(opts: ConfirmOptions): Promise<boolean> {
  return new Promise((resolve) => {
    useConfirmStore.setState({ open: true, opts, resolve })
  })
}

/** Monta una sola volta a livello app (vedi main.tsx). */
export function ConfirmHost() {
  const { open, opts, resolve } = useConfirmStore()

  const close = (value: boolean) => {
    resolve?.(value)
    useConfirmStore.setState({ open: false, opts: null, resolve: null })
  }

  return (
    <Dialog.Root open={open} onOpenChange={(o) => { if (!o) close(false) }}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-[60] data-[state=open]:animate-fade-in" />
        <Dialog.Content
          className={cn(
            'fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2',
            'w-full max-w-sm mx-4 bg-white rounded-xl shadow-modal border border-slate-200 z-[70] p-5',
            'focus:outline-none data-[state=open]:animate-fade-in'
          )}
        >
          <Dialog.Title className="text-base font-semibold text-slate-900">
            {opts?.title}
          </Dialog.Title>
          {opts?.message && (
            <Dialog.Description className="text-sm text-slate-500 mt-1.5 leading-relaxed">
              {opts.message}
            </Dialog.Description>
          )}
          <div className="flex justify-end gap-2 mt-5">
            <button
              onClick={() => close(false)}
              className="px-3 py-2 text-sm font-medium text-slate-600 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors"
            >
              {opts?.cancelLabel ?? 'Annulla'}
            </button>
            <button
              onClick={() => close(true)}
              className={cn(
                'px-3 py-2 text-sm font-medium text-white rounded-lg transition-colors',
                opts?.danger ? 'bg-danger-600 hover:bg-danger-700' : 'bg-primary-600 hover:bg-primary-700'
              )}
            >
              {opts?.confirmLabel ?? 'Conferma'}
            </button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
